import { useEffect, useRef, useState } from 'react';
import { Record as RaRecord, useNotify } from 'react-admin';

interface UseLazyReferenceOptionsArgs {
  label: string;
  loadOptions: (search: string) => Promise<RaRecord[]>;
  debounceTime?: number;
}

export const useLazyReferenceOptions = ({
  label,
  loadOptions,
  debounceTime = 500,
}: UseLazyReferenceOptionsArgs) => {
  const notify = useNotify();

  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [options, setOptions] = useState<RaRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Callers pass inline closures; read through a ref so the fetch effect
  // doesn't re-run (and refetch) on every parent render.
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, debounceTime]);

  useEffect(() => {
    // Closing mid-fetch drops that fetch's result below, so the spinner must
    // be cleared here or it never goes away.
    if (!open) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    loadOptionsRef
      .current(debouncedValue)
      .then(data => {
        if (active) {
          setOptions(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setOptions([]);
          setLoading(false);
          notify(`Failed to load ${label} options`, 'error');
        }
      });

    return () => {
      active = false;
    };
  }, [open, debouncedValue, notify, label]);

  return { open, setOpen, options, loading, setInputValue };
};
